import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ADMIN_COLORS } from '@/constants/admin';
import { ITaskItemAttributes, TASK_PRIORITY, TASK_CATEGORY_CONFIG, TASK_STATUS_CONFIG } from '@/types/admin/tasks';
import { formatTaskDate, getDueDateColor, getDueDateInfo } from '@/utils/admin/date';
import { getStatusColorHex as getStatusColorHexShared } from '@/utils/admin/tasks';
import { DesktopOverflowText } from '@/components/common/admin/desktop/DesktopOverflowText';
import { TaskAssignee } from './TaskAssignee';
import { TaskDueDate } from './TaskDueDate';
import { TaskDetailsPill } from './TaskDetailsPill';
import { TaskActions } from './TaskActions';

interface TaskRowProps {
  task: ITaskItemAttributes;
  onShowDetails: (taskId: number) => void;
  onEdit?: (taskId: number) => void;
  onDuplicate?: (taskId: number) => void;
  onArchive?: (taskId: number) => void;
  isArchived?: boolean;
  variant?: 'desktop' | 'mobile';
}

const TaskRowBase: React.FC<TaskRowProps> = ({
  task,
  onShowDetails,
  onEdit,
  onDuplicate,
  onArchive,
  isArchived = false,
  variant = 'mobile',
}) => {
  const priorityConfig = TASK_PRIORITY.find(p => p.value === task.priority) || TASK_PRIORITY[0];
  
  // Get category label, matching admin-web logic
  const getCategoryLabel = () => {
    const categoryConfig = TASK_CATEGORY_CONFIG[task.category as keyof typeof TASK_CATEGORY_CONFIG];
    return categoryConfig?.label || "Uncategorized";
  };
  
  // Get due date info and color (matching admin-web logic)
  const dueDateInfo = getDueDateInfo(task.dueDate, task.taskStatus);
  const dueDateColor = getDueDateColor(task.dueDate);
  
  // Get priority color based on admin-web mapping
  const getPriorityColor = () => {
    switch (priorityConfig.color) {
      case 'gray': return ADMIN_COLORS.TEXT_TERTIARY;
      case 'green': return '#10B981'; // green-500
      case 'yellow': return '#F59E0B'; // yellow-500  
      case 'red': return '#EF4444'; // red-500
      case 'brightRed': return '#DC2626'; // red-600
      default: return ADMIN_COLORS.TEXT_SECONDARY;
    }
  };

  const getStatusColorHex = () => getStatusColorHexShared(task.taskStatus as any);

  const getAssigneeText = () => {
    if (!task.assignees || task.assignees.length === 0) {
      return 'Unassigned';
    }
    if (task.assignees.length === 1) {
      const assignee = task.assignees[0];
      return `${assignee.firstName || ''} ${assignee.lastName || ''}`.trim() || assignee.email;
    }
    return `${task.assignees.length} assignees`;
  };

//   const getCreatorText = () => {
//     // For now, just show creator ID since we don't have full creator info
//     return task.creatorId ? `Creator ${task.creatorId}` : 'Unknown';
//   };

  const renderAssignee = () => <TaskAssignee assignees={task.assignees} />;

  const renderDesktop = () => {
    return (
      <View style={styles.desktopRow}>
        {/* Archived prior status dot (only for archived tasks) with tooltip */}
        {isArchived && (
          <View style={{ position: 'relative', marginRight: 12 }}>
            <View style={styles.tooltipAnchor}>
              <DesktopOverflowText
                text={' '}
                title={'Prior status'}
                tooltipText={TASK_STATUS_CONFIG[task.taskStatus as keyof typeof TASK_STATUS_CONFIG]?.label || 'Prior status'}
                numberOfLines={1}
                align={'center'}
              />
            </View>
            <View style={[styles.statusDotSmall, { backgroundColor: getStatusColorHex() }]} pointerEvents={'none'} />
          </View>
        )}

        {/* Title + category */}
        <View style={styles.colTitle}>
          <Text numberOfLines={1} style={styles.title}>{task.title}</Text>
          <Text numberOfLines={1} style={styles.categoryText}>{getCategoryLabel()}</Text>
        </View>

        {/* Assignee */}
        <View style={styles.colAssignee}>
          {renderAssignee()}
        </View>

        {/* Due date */}
        <View style={styles.colDue}>
          <TaskDueDate dueDate={task.dueDate as any} status={task.taskStatus as any} />
        </View>

        {/* Priority */}
        <View style={styles.colPriority}>
          <View style={[styles.priorityBadge, { backgroundColor: getPriorityColor() }]}>
            <Text style={styles.priorityBadgeText}>{priorityConfig.label}</Text>
          </View>
        </View>

        {/* Details pill */}
        <View style={styles.colDetails}>
          <TaskDetailsPill isBlocked={task.isBlocked} subtasks={task.subtasks as any} files={task.files as any} />
        </View>

        {/* Actions */}
        <View style={styles.colActions}>
          <TaskActions taskId={task.id} isArchived={isArchived} onDuplicate={onDuplicate} onArchive={onArchive} />
        </View>
      </View>
    );
  };

  return (
    <TouchableOpacity
      style={styles.container}
      onPress={() => onShowDetails(task.id)}
      activeOpacity={0.7}
    >
      <View style={styles.content}>
        {variant === 'desktop' && renderDesktop()}
        {variant !== 'desktop' && (
        <>
        {/* Main Info Row */}
        <View style={styles.mainRow}>
          <View style={styles.leftSection}>
            {/* Archived prior status dot */}
            {isArchived && (
              <View style={[styles.statusDotSmall, { backgroundColor: getStatusColorHex() }]} />
            )}
            {/* Task Title */}
            <Text style={styles.title} numberOfLines={1}>
              {task.title}
            </Text>
          </View>

          <View style={styles.rightSection}>
            {/* Due Date */}
            <View style={styles.dueDateContainer}>
              {dueDateInfo.isLongOverdue ? (
                <View style={styles.overdueBadge}>
                  <Text style={styles.overdueBadgeText}>!</Text>
                </View>
              ) : (
                <SVG_COMPONENTS.CALENDAR
                  width={14}
                  height={14}
                  color={dueDateColor}
                />
              )}
              <Text style={[styles.dueDate, { color: dueDateColor }]}>
                {formatTaskDate(task.dueDate)}
              </Text>
            </View>

            {/* Action Buttons */}
            <View style={styles.actions}>
              {onEdit && (
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={(e: any) => {
                    try { e?.stopPropagation?.(); } catch {}
                    onEdit(task.id);
                  }}
                >
                  <SVG_COMPONENTS.PENCIL
                    width={16}
                    height={16}
                    color={ADMIN_COLORS.TEXT_SECONDARY}
                  />
                </TouchableOpacity>
              )}
              
              {onDuplicate && (
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={(e: any) => {
                    try { e?.stopPropagation?.(); } catch {}
                    onDuplicate(task.id);
                  }}
                  accessibilityLabel="Duplicate Task"
                  testID={`task-row-duplicate-${task.id}`}
                >
                  <SVG_COMPONENTS.COPY width={16} height={16} />
                </TouchableOpacity>
              )}
              
              {onArchive && (
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={(e: any) => {
                    try { e?.stopPropagation?.(); } catch {}
                    onArchive(task.id);
                  }}
                  accessibilityLabel={isArchived ? "Unarchive Task" : "Archive Task"}
                  testID={`task-row-archive-${task.id}`}
                >
                  <View style={[styles.archiveIcon, isArchived && styles.archiveIconRotated]}>
                    {isArchived ? (
                      <SVG_COMPONENTS.ARCHIVE width={16} height={16} />
                    ) : (
                      <SVG_COMPONENTS.FOLDER width={16} height={16} />
                    )}
                  </View>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>

        {/* Secondary Info Row */}
        <View style={styles.secondaryRow}>
          <View style={styles.leftSecondary}>
            {/* Category */}
            <View style={styles.categoryContainer}>
              <Text style={styles.categoryText}>{getCategoryLabel()}</Text>
            </View>

            {/* Location */}
            {/* {task.location && (
              <View style={styles.locationContainer}>
                <SVG_COMPONENTS.PROPERTY
                  width={12}
                  height={12}
                  color={ADMIN_COLORS.TEXT_SECONDARY}
                />
                <Text style={styles.locationText}>{task.location}</Text>
              </View>
            )} */}
          </View>

          <View style={styles.rightSecondary}>
            {/* Assignee */}
            <Text style={styles.assigneeText}>
              {getAssigneeText()}
            </Text>
          </View>
        </View>

        {/* Indicators Row */}
        <View style={styles.indicatorsRow}>
          {/* <View style={styles.leftIndicators}>
            <Text style={styles.creatorText}>
              Created by {getCreatorText()}
            </Text>
          </View> */}

          <View style={styles.rightIndicators}>
            {/* Details pill: blocked, subtasks, files */}
            {(task.isBlocked || (task.subtasks && task.subtasks.length > 0) || (task.files && task.files.length > 0)) && (
              <View style={styles.detailsPill}>
                {task.isBlocked && (
                  <SVG_COMPONENTS.WARNING width={14} height={14} color={ADMIN_COLORS.ERROR} />
                )}
                {task.subtasks && task.subtasks.length > 0 && (
                  <View style={styles.detailsPillItem}>
                    <SVG_COMPONENTS.FOLDER width={14} height={14} />
                    <Text style={styles.detailsPillText}>
                      {(task.subtasks.filter((st: any) => !!st.completedAt).length)}/{task.subtasks.length}
                    </Text>
                  </View>
                )}
                {task.files && task.files.length > 0 && (
                  <View style={styles.detailsPillItem}>
                    <SVG_COMPONENTS.PAPERCLIP width={14} height={14} />
                    <Text style={styles.detailsPillText}>{task.files.length}</Text>
                  </View>
                )}
              </View>
            )}

            {/* Priority Badge (always visible) */}
            <View style={[styles.priorityBadge, { backgroundColor: getPriorityColor() }]}>
              <Text style={styles.priorityBadgeText}>
                {priorityConfig.label}
              </Text>
            </View>
          </View>
        </View>
        </>
        )}
      </View>
    </TouchableOpacity>
  );
};

export const TaskRow = React.memo(TaskRowBase, (prev, next) => {
  const prevAssigneesCount = prev.task.assignees?.length || 0;
  const nextAssigneesCount = next.task.assignees?.length || 0;
  const prevFirstAssignee = prev.task.assignees?.[0]
    ? `${prev.task.assignees?.[0]?.firstName || ''} ${prev.task.assignees?.[0]?.lastName || ''}`.trim()
    : '';
  const nextFirstAssignee = next.task.assignees?.[0]
    ? `${next.task.assignees?.[0]?.firstName || ''} ${next.task.assignees?.[0]?.lastName || ''}`.trim()
    : '';

  const prevSubtasksLen = prev.task.subtasks?.length || 0;
  const nextSubtasksLen = next.task.subtasks?.length || 0;
  const prevSubtasksCompleted = (prev.task.subtasks || []).filter((s: any) => !!s.completedAt).length;
  const nextSubtasksCompleted = (next.task.subtasks || []).filter((s: any) => !!s.completedAt).length;

  const prevFilesLen = prev.task.files?.length || 0;
  const nextFilesLen = next.task.files?.length || 0;

  return (
    prev.task.id === next.task.id &&
    prev.task.taskStatus === next.task.taskStatus &&
    prev.task.title === next.task.title &&
    prev.task.priority === next.task.priority &&
    prev.task.dueDate === next.task.dueDate &&
    prev.task.category === next.task.category &&
    (prev.task.isBlocked || false) === (next.task.isBlocked || false) &&
    prevAssigneesCount === nextAssigneesCount &&
    prevFirstAssignee === nextFirstAssignee &&
    prevSubtasksLen === nextSubtasksLen &&
    prevSubtasksCompleted === nextSubtasksCompleted &&
    prevFilesLen === nextFilesLen &&
    prev.isArchived === next.isArchived
  );
});

const styles = StyleSheet.create({
  container: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  content: {
    padding: 16,
  },
  mainRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  leftSection: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  priorityDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    flex: 1,
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  dueDateContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  dueDate: {
    fontSize: 12,
    // Color is set dynamically based on due date status
  },
  actions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    padding: 4,
  },
  archiveIcon: {
    // Base style for archive icon
  },
  archiveIconRotated: {
    transform: [{ rotate: '180deg' }],
  },
  secondaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  leftSecondary: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  categoryContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  categoryDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  categoryText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  locationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  locationText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  rightSecondary: {
    alignItems: 'flex-end',
  },
  assigneeText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  indicatorsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  // Desktop layout columns
  desktopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  colTitle: {
    flex: 3,
    minWidth: 0,
    paddingRight: 8,
  },
  colAssignee: {
    flex: 2,
    minWidth: 0,
    paddingRight: 8,
  },
  colDue: {
    flex: 1.5,
    minWidth: 0,
    paddingRight: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  colPriority: {
    flex: 1,
    minWidth: 80,
    alignItems: 'center',
  },
  colDetails: {
    flex: 1,
    minWidth: 100,
    alignItems: 'flex-end',
  },
  colActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  tooltipAnchor: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
    // Ensure hover events reach the tooltip on web
    // The visible icon underneath is set to pointerEvents: 'none'
  },
  iconWrapper: {
    // This wrapper allows clicks to pass through to the parent touchable
    // and lets the tooltip overlay capture hover on web
    pointerEvents: 'none' as any,
  },
  leftIndicators: {
    flex: 1,
  },
  creatorText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  rightIndicators: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statusDotSmall: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 8,
  },
  indicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  indicatorText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  overdueBadge: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  overdueBadgeText: {
    color: '#F87171',
    fontWeight: '800',
  },
  progressRing: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 6,
  },
  progressRingIconWrapper: {
    position: 'absolute',
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  priorityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 40,
  },
  priorityBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  detailsPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    height: 24,
    paddingHorizontal: 8,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  detailsPillItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  detailsPillText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
});
